type PhaseStatus = 'not started' | 'planned' | 'test UI' | 'blocked' | 'ready';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type IconName = 'lock' | 'shield' | 'server' | 'code' | 'terminal' | 'desktop' | 'deploy' | 'container' | 'backup' | 'check' | 'warning' | 'info';

interface MigrationPhase {
  phase: number;
  name: string;
  status: PhaseStatus;
  risk: RiskLevel;
  description: string;
  dependencies: string[];
  notLive: string;
  approval: string;
}

interface LinkPreview {
  label: string;
  target: string;
  disabled?: boolean;
  note?: string;
}

interface AuditEvent {
  at: string;
  action: string;
  actor: string;
  detail: string;
  ip: string;
}

interface ContainerPreview {
  name: string;
  status: string;
  image: string;
  uptime: string;
}

interface BackupRow {
  data: string;
  method: string;
  status: string;
}

interface CcJobRow {
  id: string;
  type: string;
  status: string;
  app: string;
  started: string;
}

const ADMIN_MIGRATION_PATH = '/people/test/admin-migration-dashboard';
const DISABLED_TITLE = 'Mock preview only - this action is not implemented or enabled.';

const phases: MigrationPhase[] = [
  {
    phase: 0,
    name: 'Auth & Admin Session Hardening',
    status: 'planned',
    risk: 'high',
    description: 'Introduce an elevated admin session model before any write-capable admin tools exist in Hub People.',
    dependencies: ['MFA flow', 'admin scope model', 'audit log persistence'],
    notLive: 'No elevated sessions, scope grants, or real admin actions should be active yet.',
    approval: 'Mitchell approval required before enforcement or live session issuance.',
  },
  {
    phase: 1,
    name: 'Gitea Canonical + VPS Workspace',
    status: 'planned',
    risk: 'medium',
    description: 'Keep Gitea as the source of truth while creating a non-root editable workspace clone on the VPS.',
    dependencies: ['Gitea reachable from VPS', 'hubdev Linux user', 'workspace clone path'],
    notLive: 'Do not treat the VPS workspace as canonical. It is an editable clone only.',
    approval: 'Approval needed before moving daily development from Windows to the VPS clone.',
  },
  {
    phase: 2,
    name: 'Code-Server / Web IDE',
    status: 'not started',
    risk: 'medium',
    description: 'Expose a browser editor for the VPS workspace with write access restricted to the hubdev user.',
    dependencies: ['Phase 0 admin sessions', 'Phase 1 workspace', 'reverse proxy route'],
    notLive: 'No code-server process, editor route, or write-capable web editor is enabled.',
    approval: 'Approval needed before installing or exposing code-server.',
  },
  {
    phase: 3,
    name: 'Deploy Worker',
    status: 'not started',
    risk: 'high',
    description: 'Replace manual tar and SCP deploys with commit-SHA builds triggered through a scoped worker.',
    dependencies: ['Gitea webhooks', 'deploy scripts accept SHA/tag', 'audit logging'],
    notLive: 'No production deploy trigger should be callable from this test UI.',
    approval: 'Approval needed before any worker can build, deploy, or rollback.',
  },
  {
    phase: 4,
    name: 'Structured Admin / Container Actions',
    status: 'not started',
    risk: 'high',
    description: 'Create allowlisted container actions such as restart, logs, and health views through a Docker proxy.',
    dependencies: ['docker-socket-proxy', 'action allowlist', 'scope=docker'],
    notLive: 'Raw Docker socket access must not be exposed to the frontend or editor.',
    approval: 'Approval needed before container commands are connected to real services.',
  },
  {
    phase: 5,
    name: 'Web Terminal',
    status: 'not started',
    risk: 'critical',
    description: 'Provide a scoped non-root shell for admin repair workflows that cannot be solved by panels.',
    dependencies: ['MFA', 'scope=terminal', 'idle disconnect', 'session recording or detailed audit'],
    notLive: 'No web shell should be deployed before admin session hardening is proven.',
    approval: 'Explicit approval needed because terminal access is broad and sensitive.',
  },
  {
    phase: 6,
    name: 'noVNC Break-Glass Desktop',
    status: 'not started',
    risk: 'critical',
    description: 'Offer full remote desktop only as a final emergency path after safer tools cover normal workflows.',
    dependencies: ['Cloudflare Access', 'optional WireGuard', 'kill session control', 'idle auto-lock'],
    notLive: 'No browser desktop should exist until all narrower admin paths are evaluated.',
    approval: 'Explicit break-glass approval needed before any noVNC exposure.',
  },
  {
    phase: 7,
    name: 'Backup & Restore',
    status: 'planned',
    risk: 'medium',
    description: 'Track readiness to stop depending on Windows and document restore paths for code, data, uploads, and secrets.',
    dependencies: ['Gitea backups', 'Litestream', 'upload backup path', 'secret encryption plan'],
    notLive: 'Restore actions must not be callable from the preview dashboard.',
    approval: 'Approval needed before restore automation can touch live data.',
  },
  {
    phase: 8,
    name: 'Claude Code / Codex on VPS',
    status: 'not started',
    risk: 'medium',
    description: 'Run coding agents on the VPS in isolated workspaces with output routed back into Hub People.',
    dependencies: ['API keys on VPS', 'workspace isolation', 'job queue', 'dev-activity output channel'],
    notLive: 'No agent job runner, key storage, or queue controls are active.',
    approval: 'Approval needed before agent jobs run against the VPS workspace.',
  },
];

const giteaLinks: LinkPreview[] = [
  { label: 'Repo Home', target: '/repo/hub/hub' },
  { label: 'Branches', target: '/repo/hub/hub/branches' },
  { label: 'Commits', target: '/repo/hub/hub/commits/branch/main' },
  { label: 'Pull Requests', target: '/repo/hub/hub/pulls' },
  { label: 'Settings', target: '', disabled: true, note: 'admin only, requires elevated session' },
];

const auditEvents: AuditEvent[] = [
  { at: '2026-06-03 01:15:00', action: 'admin_session_start', actor: 'mitchell', detail: 'scope=editor', ip: 'IP: 10.0.0.1' },
  { at: '2026-06-03 01:14:30', action: 'mfa_verify', actor: 'mitchell', detail: 'method=totp', ip: 'IP: 10.0.0.1' },
  { at: '2026-06-03 01:10:00', action: 'login', actor: 'mitchell', detail: 'normal_session', ip: 'IP: 10.0.0.1' },
  { at: '2026-06-03 00:45:00', action: 'admin_session_expire', actor: 'mitchell', detail: 'idle_timeout', ip: 'IP: 10.0.0.1' },
  { at: '2026-06-03 00:30:00', action: 'deploy_trigger', actor: 'mitchell', detail: 'app=hub-people', ip: 'IP: 10.0.0.1' },
];

const containers: ContainerPreview[] = [
  { name: 'hub-core', status: 'healthy', image: 'hub-core:latest', uptime: '14d' },
  { name: 'hub-people', status: 'healthy', image: 'hub-people:latest', uptime: '14d' },
  { name: 'hub-money', status: 'healthy', image: 'hub-money:latest', uptime: '14d' },
  { name: 'hub-cell', status: 'healthy', image: 'hub-cell:latest', uptime: '14d' },
  { name: 'hub-agent', status: 'healthy', image: 'hub-agent:latest', uptime: '14d' },
  { name: 'hub-mobile', status: 'healthy', image: 'hub-mobile:latest', uptime: '14d' },
  { name: 'gitea', status: 'healthy', image: 'gitea:1.21', uptime: '14d' },
  { name: 'hub-docs', status: 'stopped', image: 'hub-docs:latest', uptime: '-' },
];

const backupRows: BackupRow[] = [
  { data: 'Source code', method: 'Gitea repos + Windows clone', status: 'covered' },
  { data: 'SQLite DBs', method: 'Litestream -> S3', status: 'covered' },
  { data: 'Uploads/attachments', method: 'bind-mount + manual backup', status: 'partial' },
  { data: 'Configuration/secrets', method: 'env files on VPS', status: 'needs encryption' },
  { data: 'Gitea data', method: 'Gitea built-in backup', status: 'needs automation' },
];

const ccJobs: CcJobRow[] = [
  { id: '-', type: '-', status: 'No active jobs', app: '-', started: '-' },
];

export function isAdminMigrationPath(): boolean {
  return window.location.pathname === ADMIN_MIGRATION_PATH;
}

export function renderAdminMigrationDashboard(): void {
  injectAdminMigrationStyles();
  document.title = 'Windows to Hub People Migration Dashboard';
  document.body.classList.add('amd-body');

  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('amd-root');
  root.innerHTML = renderDashboard();

  hideShellElement('sidebar-scrim');
  hideShellElement('modal-overlay');
  hideShellElement('toast');
  wireMockControls(root);
}

function hideShellElement(id: string): void {
  const el = document.getElementById(id);
  if (el) el.style.display = 'none';
}

function wireMockControls(root: HTMLElement): void {
  const cleanButton = root.querySelector<HTMLButtonElement>('[data-dirty-state="clean"]');
  const dirtyButton = root.querySelector<HTMLButtonElement>('[data-dirty-state="dirty"]');
  const value = root.querySelector<HTMLElement>('#amd-dirty-value');
  const setDirtyState = (state: 'clean' | 'dirty'): void => {
    if (!value || !cleanButton || !dirtyButton) return;
    value.textContent = state === 'clean' ? 'Clean' : '2 modified files';
    cleanButton.classList.toggle('amd-segment-active', state === 'clean');
    dirtyButton.classList.toggle('amd-segment-active', state === 'dirty');
    cleanButton.setAttribute('aria-pressed', state === 'clean' ? 'true' : 'false');
    dirtyButton.setAttribute('aria-pressed', state === 'dirty' ? 'true' : 'false');
  };

  cleanButton?.addEventListener('click', () => setDirtyState('clean'));
  dirtyButton?.addEventListener('click', () => setDirtyState('dirty'));
}

function renderDashboard(): string {
  return `
    <main class="amd-page">
      <div class="amd-shell">
        <section class="amd-warning-banner" aria-label="Test-only warning">
          ${icon('warning')}
          <div>
            <strong>Test build only</strong>
            <span>No live admin tools, deploy triggers, terminals, Docker controls, or remote desktops are enabled from this page.</span>
          </div>
        </section>

        <header class="amd-hero">
          <div>
            <div class="amd-eyebrow">Hub People admin migration preview</div>
            <h1>Windows to Hub People Migration Dashboard</h1>
            <p>Test/admin preview - no live admin tools enabled</p>
          </div>
          <div class="amd-hero-status">
            ${badge('test UI', 'status')}
            <span>Route: ${esc(ADMIN_MIGRATION_PATH)}</span>
          </div>
        </header>

        <nav class="amd-tabs" aria-label="Migration dashboard sections">
          ${[
            ['#amd-phases', 'Phases'],
            ['#amd-source', 'Source'],
            ['#amd-session', 'Sessions'],
            ['#amd-editor', 'Editor'],
            ['#amd-deploy', 'Deploy'],
            ['#amd-containers', 'Containers'],
            ['#amd-terminal', 'Terminal'],
            ['#amd-backup', 'Backup'],
            ['#amd-agents', 'CC/Codex'],
          ].map(([href, label]) => `<a href="${href}">${label}</a>`).join('')}
        </nav>

        ${renderPhasesSection()}
        <div class="amd-two-col">
          ${renderSourceSection()}
          ${renderSessionSection()}
          ${renderEditorSection()}
          ${renderDeploySection()}
          ${renderContainerSection()}
          ${renderTerminalSection()}
          ${renderBackupSection()}
          ${renderAgentSection()}
        </div>
      </div>
    </main>
  `;
}

function renderPhasesSection(): string {
  return `
    <section id="amd-phases" class="amd-panel amd-panel-wide">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('shield')} Migration Phases Overview</span>
          <span class="amd-summary-meta">Part A</span>
        </summary>
        <div class="amd-phase-grid">
          ${phases.map(renderPhaseCard).join('')}
        </div>
      </details>
    </section>
  `;
}

function renderPhaseCard(phase: MigrationPhase): string {
  return `
    <article class="amd-phase-card">
      <div class="amd-phase-head">
        <div>
          <span class="amd-phase-number">Phase ${phase.phase}</span>
          <h3>${esc(phase.name)}</h3>
        </div>
        <div class="amd-badge-stack">
          ${badge(phase.status, 'status')}
          ${badge(phase.risk, 'risk')}
        </div>
      </div>
      <p>${esc(phase.description)}</p>
      <div class="amd-mini-block">
        <strong>Dependencies</strong>
        <span>${phase.dependencies.map(esc).join(' | ')}</span>
      </div>
      <div class="amd-mini-block amd-mini-warn">
        <strong>What must NOT be live yet</strong>
        <span>${esc(phase.notLive)}</span>
      </div>
      <div class="amd-approval">
        ${icon('lock')}
        <span>${esc(phase.approval)}</span>
      </div>
    </article>
  `;
}

function renderSourceSection(): string {
  return `
    <section id="amd-source" class="amd-panel">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('server')} Gitea Canonical Source</span>
          <span class="amd-summary-meta">Part B</span>
        </summary>
        <div class="amd-field-grid">
          ${field('VPS workspace path', '/opt/hub/workspaces/hub')}
          ${field('User', 'hubdev', 'Non-root user required for editable workspace operations.')}
          ${field('Branch', 'main')}
          ${field('Commit', '79a2c0c')}
          <div class="amd-field amd-field-wide">
            <span>Dirty status</span>
            <strong id="amd-dirty-value">Clean</strong>
            <div class="amd-segment" role="group" aria-label="Mock dirty status toggle">
              <button type="button" class="amd-segment-active" data-dirty-state="clean" aria-pressed="true">Clean</button>
              <button type="button" data-dirty-state="dirty" aria-pressed="false">2 modified files</button>
            </div>
          </div>
        </div>
        <div class="amd-link-grid" aria-label="Gitea quick links">
          ${giteaLinks.map(renderMockLink).join('')}
        </div>
        ${infoBox('Gitea remains the source of truth. VPS workspace is an editable clone, not canonical. Windows remains a clone during transition.')}
      </details>
    </section>
  `;
}

function renderSessionSection(): string {
  const scopes: Array<[string, string]> = [
    ['editor', 'code-server write access'],
    ['terminal', 'shell access'],
    ['deploy', 'production deploy triggers'],
    ['docker', 'container management'],
    ['files', 'file manager write access'],
    ['noVNC', 'remote desktop'],
    ['backups', 'backup restore operations'],
  ];
  return `
    <section id="amd-session" class="amd-panel">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('lock')} Admin Session Hardening Preview</span>
          <span class="amd-summary-meta">Part C</span>
        </summary>
        <div class="amd-subgrid">
          <div class="amd-subpanel">
            <h3>Current Session</h3>
            <div class="amd-row-list">
              ${field('Type', 'Normal')}
              ${field('MFA', 'Not enforced', 'Mock badge only; MFA must be enforced before elevated admin sessions.')}
              ${field('Timeout', 'None configured')}
            </div>
          </div>
          <div class="amd-subpanel">
            <h3>Elevated Admin Session</h3>
            <p>MFA is required before a short-lived elevated session can grant scoped admin capabilities.</p>
            <div class="amd-action-row">
              ${disabledButton('Request Admin Session', 'mock - not implemented')}
              ${disabledButton('Lock Now', 'mock')}
            </div>
            <ul class="amd-list">
              <li>15-minute TTL</li>
              <li>5-minute idle timeout</li>
              <li>Every grant and action written to audit log</li>
            </ul>
          </div>
        </div>
        <div class="amd-scope-grid">
          ${scopes.map(([name, description]) => `<span class="amd-scope">${icon('lock')} <strong>${esc(name)}</strong> ${esc(description)}</span>`).join('')}
        </div>
        <div class="amd-table-wrap">
          <table>
            <caption>Mock Audit Log</caption>
            <thead><tr><th>Time</th><th>Event</th><th>User</th><th>Detail</th><th>IP</th></tr></thead>
            <tbody>${auditEvents.map((event) => `
              <tr>
                <td>${esc(event.at)}</td>
                <td>${esc(event.action)}</td>
                <td>${esc(event.actor)}</td>
                <td>${esc(event.detail)}</td>
                <td>${esc(event.ip)}</td>
              </tr>
            `).join('')}</tbody>
          </table>
        </div>
      </details>
    </section>
  `;
}

function renderEditorSection(): string {
  return `
    <section id="amd-editor" class="amd-panel">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('code')} Code Editor</span>
          <span class="amd-summary-meta">Part D ${badge('not started', 'status')}</span>
        </summary>
        <div class="amd-field-grid">
          ${field('Intended route', '/people/admin/editor')}
          ${field('Intended host', 'code-server running as hubdev on VPS')}
          ${field('Workspace', '/opt/hub/workspaces/hub')}
          ${field('User', 'hubdev (non-root)')}
          ${field('RAM estimate', '500 MB - 2 GB with Rust analyzer')}
        </div>
        <div class="amd-action-row">
          ${disabledButton('Start code-server')}
          ${disabledButton('Stop code-server')}
          ${disabledButton('Open Editor')}
        </div>
        <ul class="amd-list">
          <li>Requires elevated admin session with scope=editor</li>
          <li>Runs as non-root hubdev user</li>
          <li>No Docker socket access from editor</li>
        </ul>
        ${warnBox('Code-server is NOT live. This is a preview of the planned interface.')}
      </details>
    </section>
  `;
}

function renderDeploySection(): string {
  const log = [
    '[deploy] No deploy worker configured',
    '[deploy] Current flow: manual tar -> SCP -> docker build on VPS',
    '[deploy] Target flow: commit-SHA deploy from Gitea webhook',
    '[deploy] Approval required before any production deploy',
  ].join('\n');
  return `
    <section id="amd-deploy" class="amd-panel">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('deploy')} Deploy Worker</span>
          <span class="amd-summary-meta">Part E ${badge('not started', 'status')}</span>
        </summary>
        <div class="amd-form-grid">
          <label>App selector
            <select disabled title="${escAttr(DISABLED_TITLE)}">
              ${['hub-people', 'hub-money', 'hub-cell', 'hub-core', 'hub-docs', 'hub-agent'].map((app) => `<option>${esc(app)}</option>`).join('')}
            </select>
          </label>
          <label>Branch/SHA
            <input disabled placeholder="main" title="${escAttr(DISABLED_TITLE)}">
          </label>
        </div>
        <div class="amd-action-row">
          ${disabledButton('Build')}
          ${disabledButton('Deploy')}
          ${disabledButton('Rollback')}
        </div>
        <div class="amd-subgrid">
          <div class="amd-subpanel">
            <h3>Mock Build Status</h3>
            ${field('Status', 'No active build')}
            ${field('Last build', 'manual tar+SCP flow (current)')}
            ${field('Target', 'Commit-based Gitea webhook builds')}
          </div>
          <label class="amd-log-label">Mock Deploy Log
            <textarea readonly>${esc(log)}</textarea>
          </label>
        </div>
        ${warnBox('Deploy worker is NOT live. Deploys still use manual flow. This preview shows the planned interface.')}
      </details>
    </section>
  `;
}

function renderContainerSection(): string {
  return `
    <section id="amd-containers" class="amd-panel amd-panel-wide">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('container')} Container Dashboard</span>
          <span class="amd-summary-meta">Part F ${badge('test UI', 'status')}</span>
        </summary>
        <div class="amd-table-wrap">
          <table>
            <thead><tr><th>Container</th><th>Status</th><th>Image</th><th>Uptime</th><th>Actions</th></tr></thead>
            <tbody>${containers.map((container) => `
              <tr>
                <td>${esc(container.name)}</td>
                <td>${badge(container.status === 'healthy' ? 'ready' : 'not started', 'status')} ${esc(container.status)}</td>
                <td>${esc(container.image)}</td>
                <td>${esc(container.uptime)}</td>
                <td><div class="amd-action-row amd-action-row-compact">${disabledButton('Restart')}${disabledButton('View Logs')}${disabledButton('Stop')}</div></td>
              </tr>
            `).join('')}</tbody>
          </table>
        </div>
        ${warnBox('Docker socket access is root-equivalent. Production implementation will use docker-socket-proxy with action allowlisting. Raw socket access will NOT be exposed.')}
        ${infoBox('Container data shown is MOCK. Real container status requires backend integration with scoped Docker API proxy.')}
      </details>
    </section>
  `;
}

function renderTerminalSection(): string {
  return `
    <section id="amd-terminal" class="amd-panel">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('terminal')} Terminal / noVNC Preview</span>
          <span class="amd-summary-meta">Part G</span>
        </summary>
        <div class="amd-subgrid">
          <div class="amd-subpanel">
            <h3>Web Terminal</h3>
            ${field('Status', 'Not deployed')}
            ${field('Technology', 'ttyd or xterm.js (planned)')}
            ${disabledButton('Open Terminal')}
            <ul class="amd-list">
              <li>admin session</li>
              <li>scope=terminal</li>
              <li>audit logging</li>
              <li>non-root shell</li>
              <li>5-minute idle auto-disconnect</li>
            </ul>
          </div>
          <div class="amd-subpanel">
            <h3>${icon('desktop')} noVNC Break-Glass Desktop</h3>
            ${field('Status', 'Not deployed - intentionally last')}
            <p>noVNC provides full desktop access and is the hardest to scope securely. Web IDE + terminal + admin panels solve most workflows without the attack surface of a full desktop.</p>
            <div class="amd-action-row">
              ${disabledButton('Open Desktop')}
              ${disabledButton('Emergency Kill Session', 'mock')}
            </div>
            <ul class="amd-list">
              <li>admin session + Cloudflare Access + optional WireGuard</li>
              <li>5-minute idle auto-lock</li>
            </ul>
          </div>
        </div>
        ${warnBox('Neither terminal nor noVNC are live. These previews show the planned interface and security requirements.')}
      </details>
    </section>
  `;
}

function renderBackupSection(): string {
  const checks = [
    'Source code pushed to Gitea (all repos)',
    'VPS workspace clone created at /opt/hub/workspaces/hub',
    'hubdev user created on VPS',
    'code-server installed and tested',
    'Deploy scripts accept commit SHA/tag',
    'Claude Code running on VPS',
    'Windows-specific scripts ported or removed',
    'Personal files backed up (non-code)',
    'App/game inventory documented',
  ];
  return `
    <section id="amd-backup" class="amd-panel">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('backup')} Backup / Move-off-Windows Preview</span>
          <span class="amd-summary-meta">Part H</span>
        </summary>
        <div class="amd-checklist" aria-label="Windows PC Dependency Checklist">
          <h3>Windows PC Dependency Checklist</h3>
          ${checks.map((item) => `<label><input type="checkbox" disabled title="${escAttr(DISABLED_TITLE)}"> <span>${esc(item)}</span></label>`).join('')}
        </div>
        <div class="amd-table-wrap">
          <table>
            <caption>Backup Coverage</caption>
            <thead><tr><th>Data</th><th>Backup Method</th><th>Status</th></tr></thead>
            <tbody>${backupRows.map((row) => `
              <tr>
                <td>${esc(row.data)}</td>
                <td>${esc(row.method)}</td>
                <td>${esc(row.status)}</td>
              </tr>
            `).join('')}</tbody>
          </table>
        </div>
        ${infoBox('This checklist tracks readiness to stop depending on the Windows PC. All items must be checked before Windows becomes optional.')}
      </details>
    </section>
  `;
}

function renderAgentSection(): string {
  return `
    <section id="amd-agents" class="amd-panel">
      <details open>
        <summary>
          <span class="amd-summary-title">${icon('code')} CC/Codex Job Manager</span>
          <span class="amd-summary-meta">Part I ${badge('planned', 'status')}</span>
        </summary>
        <p>Run Claude Code and Codex on the VPS instead of Windows. Jobs are containerized, workspace-scoped, and output to dev-activity channel.</p>
        <div class="amd-table-wrap">
          <table>
            <thead><tr><th>Job ID</th><th>Type</th><th>Status</th><th>App</th><th>Started</th></tr></thead>
            <tbody>${ccJobs.map((job) => `
              <tr>
                <td>${esc(job.id)}</td>
                <td>${esc(job.type)}</td>
                <td>${esc(job.status)}</td>
                <td>${esc(job.app)}</td>
                <td>${esc(job.started)}</td>
              </tr>
            `).join('')}</tbody>
          </table>
        </div>
        <div class="amd-action-row">
          ${disabledButton('New Job')}
          ${disabledButton('Kill Job')}
          ${disabledButton('Pause Queue')}
        </div>
        <div class="amd-field-grid">
          ${field('API usage', 'not tracked in test UI')}
          ${field('Context warning', 'not tracked in test UI')}
          ${field('Compact/recovery', 'not tracked in test UI')}
        </div>
        ${infoBox('Real job management requires Anthropic API key on VPS, workspace isolation, and output routing. This preview shows the planned interface.')}
      </details>
    </section>
  `;
}

function renderMockLink(link: LinkPreview): string {
  if (link.disabled) {
    return `<button type="button" disabled title="${escAttr(link.note ?? DISABLED_TITLE)}">${esc(link.label)} <span>${esc(link.note ?? 'disabled')}</span></button>`;
  }
  return `<button type="button" disabled title="${escAttr(`Will link to ${link.target}`)}">${esc(link.label)} <span>will link to ${esc(link.target)}</span></button>`;
}

function field(label: string, value: string, help?: string): string {
  return `
    <div class="amd-field">
      <span>${esc(label)}</span>
      <strong>${esc(value)}</strong>
      ${help ? `<em>${esc(help)}</em>` : ''}
    </div>
  `;
}

function disabledButton(label: string, suffix?: string): string {
  const fullLabel = suffix ? `${label} - ${suffix}` : label;
  return `<button class="amd-button" type="button" disabled title="${escAttr(DISABLED_TITLE)}">${esc(fullLabel)}</button>`;
}

function badge(value: string, kind: 'status' | 'risk'): string {
  const classValue = value.toLowerCase().replace(/\s+/g, '-');
  return `<span class="amd-badge amd-${kind}-${classValue}">${kind === 'risk' ? `${esc(value)} risk` : esc(value)}</span>`;
}

function infoBox(text: string): string {
  return `<div class="amd-info-box">${icon('info')}<span>${esc(text)}</span></div>`;
}

function warnBox(text: string): string {
  return `<div class="amd-warn-box">${icon('warning')}<span>${esc(text)}</span></div>`;
}

function icon(name: IconName): string {
  const paths: Record<IconName, string> = {
    lock: '<path d="M7 10V7a5 5 0 0 1 10 0v3"/><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M12 14v3"/>',
    shield: '<path d="M12 3 19 6v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-3Z"/><path d="m9 12 2 2 4-5"/>',
    server: '<rect x="4" y="4" width="16" height="6" rx="2"/><rect x="4" y="14" width="16" height="6" rx="2"/><path d="M8 7h.01M8 17h.01"/>',
    code: '<path d="m8 9-4 3 4 3"/><path d="m16 9 4 3-4 3"/><path d="m14 5-4 14"/>',
    terminal: '<path d="m5 7 5 5-5 5"/><path d="M12 17h7"/>',
    desktop: '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/>',
    deploy: '<path d="M12 3v12"/><path d="m7 8 5-5 5 5"/><path d="M5 17v2h14v-2"/>',
    container: '<path d="M4 7h16v12H4z"/><path d="M4 11h16M8 7v12M12 7v12M16 7v12"/><path d="M7 4h10v3H7z"/>',
    backup: '<path d="M12 5a7 7 0 1 1-6.3 4"/><path d="M5 5v4h4"/><path d="M12 8v5l3 2"/>',
    check: '<path d="m5 12 5 5L20 7"/>',
    warning: '<path d="M12 3 22 20H2L12 3Z"/><path d="M12 9v5M12 17h.01"/>',
    info: '<circle cx="12" cy="12" r="10"/><path d="M12 10v6M12 7h.01"/>',
  };
  return `<svg class="amd-icon amd-icon-${name}" viewBox="0 0 24 24" aria-hidden="true">${paths[name]}</svg>`;
}

function esc(value: string): string {
  const el = document.createElement('span');
  el.textContent = value;
  return el.innerHTML;
}

function escAttr(value: string): string {
  return esc(value).replace(/"/g, '&quot;');
}

function injectAdminMigrationStyles(): void {
  if (document.getElementById('admin-migration-dashboard-styles')) return;
  const style = document.createElement('style');
  style.id = 'admin-migration-dashboard-styles';
  style.textContent = `
body.amd-body {
  display: block !important;
  min-height: 100dvh;
  height: auto !important;
  overflow-y: auto !important;
  background: var(--surface-2) !important;
}
.amd-root {
  display: block !important;
  width: 100% !important;
  min-height: 100dvh !important;
  height: auto !important;
  overflow: visible !important;
  flex: none !important;
  background: var(--surface-2);
}
.amd-page {
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: var(--surface-2);
  color: var(--text-primary);
  font-family: var(--font-sans);
  padding: 24px;
}
.amd-shell {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}
.amd-warning-banner,
.amd-hero,
.amd-panel,
.amd-subpanel,
.amd-field,
.amd-info-box,
.amd-warn-box {
  box-sizing: border-box;
}
.amd-warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid var(--warning);
  border-radius: var(--radius-md);
  background: var(--warning-soft);
  color: var(--text-primary);
}
.amd-warning-banner strong,
.amd-warning-banner span {
  display: block;
}
.amd-warning-banner span {
  color: var(--text-secondary);
  margin-top: 2px;
  line-height: 1.4;
}
.amd-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 22px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  background: var(--surface-1);
}
.amd-eyebrow,
.amd-summary-meta,
.amd-phase-number,
.amd-field span,
.amd-field em,
.amd-log-label,
.amd-form-grid label {
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 700;
}
.amd-eyebrow,
.amd-phase-number {
  text-transform: uppercase;
}
.amd-hero h1 {
  margin: 6px 0 6px;
  color: var(--text-primary);
  font-size: 28px;
  line-height: 1.15;
}
.amd-hero p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
}
.amd-hero-status {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 8px;
  color: var(--text-muted);
  font-size: 12px;
  text-align: right;
}
.amd-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 14px 0;
}
.amd-tabs a {
  flex: 0 0 auto;
  padding: 8px 10px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface-1);
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
}
.amd-tabs a:hover {
  background: var(--surface-1-hover);
  color: var(--text-primary);
}
.amd-two-col {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.amd-panel {
  min-width: 0;
  margin-bottom: 14px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  background: var(--surface-1);
  overflow: hidden;
}
.amd-panel-wide {
  grid-column: 1 / -1;
}
.amd-panel details {
  display: block;
}
.amd-panel summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface-0);
  list-style: none;
}
.amd-panel summary::-webkit-details-marker {
  display: none;
}
.amd-summary-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 800;
}
.amd-summary-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.amd-panel details > :not(summary) {
  margin-left: 16px;
  margin-right: 16px;
}
.amd-panel details > :last-child {
  margin-bottom: 16px;
}
.amd-phase-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}
.amd-phase-card {
  min-width: 0;
  padding: 14px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  background: var(--surface-2);
}
.amd-phase-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.amd-phase-card h3,
.amd-subpanel h3,
.amd-checklist h3 {
  margin: 4px 0 0;
  color: var(--text-primary);
  font-size: 15px;
  line-height: 1.25;
}
.amd-phase-card p,
.amd-subpanel p,
#amd-agents p {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.45;
}
.amd-badge-stack,
.amd-action-row,
.amd-scope-grid,
.amd-link-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.amd-badge {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 8px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
  white-space: nowrap;
}
.amd-status-not-started {
  background: var(--surface-3);
  color: var(--text-muted);
}
.amd-status-planned {
  background: var(--info-soft);
  color: var(--info);
}
.amd-status-test-ui {
  background: var(--accent-soft);
  color: var(--accent);
}
.amd-status-blocked {
  background: var(--danger-soft);
  color: var(--danger);
}
.amd-status-ready {
  background: var(--accent);
  color: var(--text-primary);
}
.amd-risk-low {
  background: var(--accent-soft);
  color: var(--accent);
}
.amd-risk-medium {
  background: var(--warning-soft);
  color: var(--warning);
}
.amd-risk-high {
  background: var(--danger-soft);
  color: var(--danger);
}
.amd-risk-critical {
  background: var(--danger);
  color: var(--text-primary);
  animation: amdPulse 1.6s ease-in-out infinite;
}
@keyframes amdPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .72; }
}
.amd-mini-block,
.amd-approval {
  margin-top: 10px;
  padding: 10px;
  border-radius: var(--radius-sm);
  background: var(--surface-3);
}
.amd-mini-block strong,
.amd-mini-block span,
.amd-approval span {
  display: block;
}
.amd-mini-block strong {
  color: var(--text-primary);
  font-size: 12px;
}
.amd-mini-block span,
.amd-approval span {
  margin-top: 3px;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.35;
}
.amd-mini-warn {
  background: var(--warning-soft);
  border: 1px solid var(--warning);
}
.amd-approval {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid var(--border-subtle);
}
.amd-field-grid,
.amd-form-grid,
.amd-subgrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}
.amd-field {
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface-2);
}
.amd-field-wide {
  grid-column: 1 / -1;
}
.amd-field span,
.amd-field strong,
.amd-field em {
  display: block;
}
.amd-field strong {
  margin-top: 4px;
  color: var(--text-primary);
  font-size: 14px;
  overflow-wrap: anywhere;
}
.amd-field em {
  margin-top: 6px;
  font-style: normal;
  line-height: 1.35;
}
.amd-segment {
  display: inline-flex;
  gap: 2px;
  margin-top: 8px;
  padding: 3px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface-1);
}
.amd-segment button {
  border: 0;
  border-radius: var(--radius-sm);
  background: var(--surface-1);
  color: var(--text-secondary);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 8px;
}
.amd-segment button.amd-segment-active {
  background: var(--accent-soft);
  color: var(--accent);
}
.amd-link-grid {
  margin-top: 12px;
}
.amd-link-grid button,
.amd-button {
  min-height: 32px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--surface-3);
  color: var(--text-primary);
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  padding: 7px 10px;
}
.amd-link-grid button span {
  display: block;
  margin-top: 2px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 600;
}
.amd-link-grid button:disabled,
.amd-button:disabled,
.amd-form-grid input:disabled,
.amd-form-grid select:disabled,
.amd-checklist input:disabled {
  cursor: not-allowed;
  opacity: .58;
}
.amd-info-box,
.amd-warn-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 11px 12px;
  border-radius: var(--radius-sm);
  font-size: 13px;
  line-height: 1.4;
}
.amd-info-box {
  border: 1px solid var(--info);
  background: var(--info-soft);
  color: var(--text-primary);
}
.amd-warn-box {
  border: 1px solid var(--warning);
  background: var(--warning-soft);
  color: var(--text-primary);
}
.amd-subpanel {
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface-2);
}
.amd-row-list .amd-field {
  margin-top: 8px;
}
.amd-action-row {
  align-items: center;
  margin-top: 12px;
}
.amd-action-row-compact {
  margin-top: 0;
  gap: 5px;
}
.amd-action-row-compact .amd-button {
  min-height: 28px;
  padding: 4px 7px;
}
.amd-list {
  margin: 12px 0 0;
  padding-left: 20px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}
.amd-scope-grid {
  margin-top: 12px;
}
.amd-scope {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface-2);
  color: var(--text-secondary);
  font-size: 12px;
}
.amd-scope strong {
  color: var(--text-primary);
}
.amd-table-wrap {
  width: 100%;
  margin-top: 14px;
  overflow-x: auto;
}
.amd-table-wrap table {
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
  color: var(--text-secondary);
  font-size: 13px;
}
.amd-table-wrap caption {
  margin-bottom: 8px;
  color: var(--text-primary);
  font-weight: 800;
  text-align: left;
}
.amd-table-wrap th,
.amd-table-wrap td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--border-subtle);
  text-align: left;
  vertical-align: top;
}
.amd-table-wrap th {
  color: var(--text-primary);
  background: var(--surface-0);
  font-size: 12px;
  text-transform: uppercase;
}
.amd-form-grid label,
.amd-log-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.amd-form-grid input,
.amd-form-grid select,
.amd-log-label textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface-2);
  color: var(--text-primary);
  font: inherit;
  font-size: 13px;
  padding: 9px 10px;
}
.amd-log-label textarea {
  min-height: 128px;
  resize: vertical;
  line-height: 1.45;
}
.amd-checklist {
  margin-top: 14px;
  padding: 12px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface-2);
}
.amd-checklist label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.35;
}
.amd-checklist input {
  margin-top: 2px;
  accent-color: var(--accent);
}
.amd-icon {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
}
.amd-icon-warning {
  color: var(--warning);
}
.amd-icon-info {
  color: var(--info);
}
@media (max-width: 1120px) {
  .amd-phase-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .amd-two-col {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 720px) {
  .amd-page {
    padding: 14px;
  }
  .amd-hero,
  .amd-phase-head {
    flex-direction: column;
  }
  .amd-hero-status {
    align-items: flex-start;
    text-align: left;
  }
  .amd-hero h1 {
    font-size: 23px;
  }
  .amd-phase-grid,
  .amd-field-grid,
  .amd-form-grid,
  .amd-subgrid {
    grid-template-columns: 1fr;
  }
  .amd-panel summary {
    align-items: flex-start;
    flex-direction: column;
  }
  .amd-panel details > :not(summary) {
    margin-left: 12px;
    margin-right: 12px;
  }
}
`;
  document.head.appendChild(style);
}
